import {
	MAX_RETRY_ATTEMPTS,
	MAX_WAIT_BEFORE_RETRY_MS,
	WAIT_BEFORE_RETRY_MS,
} from '@/client/downloads/bulkDownloadToggles'
import { isAbortError } from '@/utils/isAbortError'

type FetchImpl = (
	input: RequestInfo | URL,
	init?: RequestInit
) => Promise<Response>

export type DownloadBlobWithRangeResumeOptions = {
	abortSignal?: AbortSignal
	fallbackFileName: string
	fetchImpl?: FetchImpl
	maxResumeAttempts?: number
	onProgress: (percentComplete: number) => void
	resumeBackoffMs?: number
	resumeOnDisconnect?: boolean
	sleep?: (milliseconds: number) => Promise<void>
	url: string
}

export type DownloadBlobWithRangeResumeResult = {
	blob: Blob
	fileName: string
}

class RangeNotHonouredError extends Error {
	constructor() {
		super(
			'Server did not honour Range; refusing to restart download from byte 0'
		)
		this.name = 'RangeNotHonouredError'
	}
}

const sleepFor = (milliseconds: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, milliseconds)
	})

const throwIfAborted = (abortSignal?: AbortSignal): void => {
	if (abortSignal?.aborted) {
		throw new DOMException('The operation was aborted.', 'AbortError')
	}
}

const parseTotalBytesFromContentRange = (
	contentRange: string | null
): number | null => {
	if (!contentRange) {
		return null
	}

	const match = /bytes\s+\d+-\d+\/(\d+)/i.exec(contentRange)

	if (!match) {
		return null
	}

	return parseInt(match[1], 10)
}

const resolveTotalBytes = (
	response: Response,
	byteOffset: number
): number | null => {
	if (byteOffset > 0) {
		return parseTotalBytesFromContentRange(
			response.headers.get('Content-Range')
		)
	}

	const contentLength = response.headers.get('Content-Length')

	if (!contentLength) {
		return null
	}

	return parseInt(contentLength, 10)
}

const readResponseChunks = async (
	response: Response,
	chunks: Uint8Array[],
	byteOffset: number,
	totalBytes: number | null,
	onProgress: (percentComplete: number) => void,
	abortSignal?: AbortSignal
): Promise<number> => {
	const reader = response.body?.getReader()

	if (!reader) {
		const blob = await response.blob()
		const bytes = new Uint8Array(await blob.arrayBuffer())
		chunks.push(bytes)
		const receivedLength = byteOffset + bytes.length

		if (totalBytes && totalBytes > 0) {
			onProgress(100)
		}

		return receivedLength
	}

	let receivedLength = byteOffset
	const abortRead = (): void => {
		void reader.cancel()
	}

	abortSignal?.addEventListener('abort', abortRead, { once: true })

	try {
		let streamDone = false

		while (!streamDone) {
			throwIfAborted(abortSignal)

			const { done, value } = await reader.read()
			streamDone = done

			if (done || !value) {
				continue
			}

			chunks.push(value)
			receivedLength += value.length

			if (totalBytes && totalBytes > 0) {
				onProgress(parseFloat(((receivedLength / totalBytes) * 100).toFixed(2)))
			}
		}
	} finally {
		abortSignal?.removeEventListener('abort', abortRead)
	}

	throwIfAborted(abortSignal)

	if (totalBytes && totalBytes > 0) {
		onProgress(100)
	}

	return receivedLength
}

const isServerErrorStatus = (statusCode: number): boolean =>
	statusCode >= 500 && statusCode < 600

export const downloadBlobWithRangeResume = async ({
	url,
	fallbackFileName,
	onProgress,
	abortSignal,
	resumeOnDisconnect = true,
	maxResumeAttempts = MAX_RETRY_ATTEMPTS,
	resumeBackoffMs = WAIT_BEFORE_RETRY_MS,
	fetchImpl = fetch,
	sleep = sleepFor,
}: DownloadBlobWithRangeResumeOptions): Promise<DownloadBlobWithRangeResumeResult> => {
	const chunks: Uint8Array[] = []
	let attempt = 0
	let backoffMs = resumeBackoffMs

	const waitBeforeRetry = async (): Promise<void> => {
		throwIfAborted(abortSignal)
		await sleep(backoffMs)
		backoffMs = Math.min(backoffMs * 2, MAX_WAIT_BEFORE_RETRY_MS)
	}

	const canRetry = (): boolean =>
		resumeOnDisconnect && attempt < maxResumeAttempts

	while (attempt < maxResumeAttempts) {
		throwIfAborted(abortSignal)
		attempt += 1

		const byteOffset = chunks.reduce(
			(totalBytes, chunk) => totalBytes + chunk.length,
			0
		)

		const requestHeaders: Record<string, string> = {
			Accept: 'application/octet-stream',
		}

		if (byteOffset > 0) {
			requestHeaders.Range = `bytes=${byteOffset}-`
		}

		let response: Response

		try {
			response = await fetchImpl(url, {
				headers: requestHeaders,
				method: 'GET',
				...(abortSignal && { signal: abortSignal }),
			})
		} catch (error) {
			if (isAbortError(error)) {
				throw error
			}

			if (!canRetry()) {
				throw error instanceof Error ? error : new Error('Download failed')
			}

			await waitBeforeRetry()
			continue
		}

		if (byteOffset > 0 && response.status === 200) {
			throw new RangeNotHonouredError()
		}

		if (!response.ok && response.status !== 206) {
			if (isServerErrorStatus(response.status) && canRetry()) {
				await waitBeforeRetry()
				continue
			}

			throw new Error('Download failed')
		}

		try {
			const totalBytes = resolveTotalBytes(response, byteOffset)

			await readResponseChunks(
				response,
				chunks,
				byteOffset,
				totalBytes,
				onProgress,
				abortSignal
			)

			return {
				blob: new Blob(chunks as BlobPart[]),
				fileName: fallbackFileName,
			}
		} catch (error) {
			if (isAbortError(error) || error instanceof RangeNotHonouredError) {
				throw error
			}

			if (!canRetry()) {
				throw error instanceof Error ? error : new Error('Download failed')
			}

			await waitBeforeRetry()
		}
	}

	throw new Error('Download failed')
}
